import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Input } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { useNavigate, useParams } from 'react-router-dom';

import * as Yup from 'yup';

import { showError, showToast } from '../../constants/toasts';
import { Loader } from '../../helpers';
import MainContainer from '../../layout/MainContainer';
import 'react-phone-input-2/lib/bootstrap.css';
import { useLazyGetUserByIdQuery, useUpdateUserProfileMutation } from '../../service/auth';
import { CommonBody, UserById } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { UploadMedia } from '../../utils/mediaUpload';
import { isNumber } from '../../utils/validations';

const EditProfile = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [getDetail] = useLazyGetUserByIdQuery();
  const [details, setUserDetails] = useState<UserById>();

  const [updateUser] = useUpdateUserProfileMutation();

  const handleProfileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfileImage('');
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.image) {
      setProfileImage(imageUrl?.data?.image || '');
    }
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: details?.name || '',
      Bio: details?.Bio || '',
      address: details?.addressObject?.address || '',
      email: details?.email || '',
      dialCode: details?.dialCode || '',
      city: details?.addressObject?.city || '',
      country: details?.addressObject?.country || '',
      pincode: details?.addressObject?.pincode || '',
      state: details?.addressObject?.state || '',
      accountHolederName: details?.bankDetails?.accountHolederName || '',
      bankName: details?.bankDetails?.bankName || '',
      branchName: details?.bankDetails?.branchName || '',
      bankCode: details?.bankDetails?.bankCode || '',
      phone: details?.phoneNo || '',
    },

    validationSchema: Yup.object({
      name: Yup.string()
        .required('This field is required')
        .min(2, 'Minimum 2 characters are required'),
      Bio:
        type === 'dealer'
          ? Yup.string()
              .required('This field is required')
              .min(10, 'Minimum 10 characters are required')
          : Yup.string().notRequired(),

      address: Yup.string()
        .required('This field is required')
        .min(3, 'Minimum 3 characters are required'),
      // accountHolederName: Yup.string()
      //   .required("This field is required")
      //   .min(3, "Minimum 3 characters are required"),
      // bankName: Yup.string()
      //   .required("This field is required")
      //   .min(3, "Minimum 3 characters are required"),
      // branchName: Yup.string()
      //   .required("This field is required")
      //   .min(3, "Minimum 3 characters are required"),
      // bankCode: Yup.string()
      //   .required("This field is required")
      //   .min(3, "Minimum 3 characters are required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        image: profileImage,
        latitude: 0,
        longitude: 0,
        name: values?.name || '',
        Bio: values?.Bio || '',
        address: values?.address || '',
        city: values?.city || '',
        country: values?.country || '',
        pincode: values?.pincode || '',
        state: values?.state || '',
        accountHolederName: values?.accountHolederName || '',
        bankName: values?.bankName || '',
        branchName: values?.branchName || '',
        bankCode: values?.bankCode || '',
      };

      if (id) {
        try {
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateUser({
            body: encryptedBody,
            id,
          }).unwrap();
          if (response?.statusCode === 200) {
            showToast('Profile updated successfully');

            navigate(-1);
          }
        } catch (error: any) {
          showError(error?.data?.message || '');
          console.error(error);
        }
      }
    },
  });

  const getUserDetails = async (id: string) => {
    setLoading(true);
    try {
      const response = await getDetail({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        setUserDetails(response?.data);
        setProfileImage(response?.data?.image || '');
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getUserDetails(id);
    }
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>Edit Profile Detail</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> - Registrations - Edit
            Profile Detail
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Profile Image</label>
              <div className="upload_box">
                <label>
                  {profileImage && (
                    <IconButton className="crossIcon" onClick={() => setProfileImage('')}>
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img
                      src={profileImage ? profileImage : '/static/images/user_placeholder.png'}
                      alt="Upload"
                    />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: 'none' }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: 'image/png,image/jpeg',
                          }}
                          onChange={(e: any) => {
                            handleProfileUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
              </div>
            </div>
            {/* {type === "dealer" || type === "store" ? (
              <div className="control_group w_50">
                <label>Logo Image</label>
                <div className="upload_box">
                  <label>
                    {logoImage && (
                      <IconButton
                        className="crossIcon"
                        onClick={() => setLogoImage("")}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                    <figure>
                      <img
                        src={
                          logoImage
                            ? logoImage
                            : "/static/images/user_attachmen2.jpg"
                        }
                        alt="Upload"
                      />
                    </figure>
                    <IconButton className="uploadIcon">
                      <label htmlFor="icon-button-file">
                        <Button component="span" className="upload_image_btn">
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt="Upload"
                          />
                          <Input
                            sx={{ display: "none" }}
                            id="icon-button-file"
                            type="file"
                            inputProps={{
                              accept: "image/png,image/jpeg",
                            }}
                            onChange={(e: any) => {
                              handleLogoUpload(e);
                              e.target.value = null;
                            }}
                          />
                        </Button>
                      </label>
                    </IconButton>
                  </label>
                </div>
              </div>
            ) : (
              <div className="control_group w_50" />
            )} */}
            {/*  */}
            <div className="control_group w_50" />
            <div className="control_group w_50">
              <label>Full name</label>
              <TextField
                variant="outlined"
                placeholder="Full name"
                className="text_field"
                fullWidth
                hiddenLabel
                name="name"
                id="name"
                inputProps={{ maxLength: 80 }}
                value={formik.values.name}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
            {/* {type === "dealer" || type === "store" ? (
              <div className="control_group w_50">
                <label>Store Name</label>
                <TextField
                  variant="outlined"
                  placeholder="Store Name"
                  fullWidth
                  hiddenLabel
                />
              </div>
            ) : null} */}

            <div className="control_group w_50">
              <label>E-mail</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                disabled
                className="text_field"
                value={formik.values.email}
              />
            </div>
            <div className="control_group w_50">
              <label>Phone Number</label>
              <PhoneInput
                enableSearch
                value={formik.values.dialCode + formik.values.phone}
                country={'us'}
                inputClass="phoneInput"
                inputStyle={{ width: '100%' }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
                disabled
                // className="text_field"
              />
            </div>

            {type === 'dealer' ? (
              <div className="control_group w_100">
                <label>Bio</label>
                <TextField
                  type="date"
                  variant="outlined"
                  placeholder="Enter here"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={4}
                  hiddenLabel
                  name="Bio"
                  id="Bio"
                  className="text_field"
                  inputProps={{ maxLength: 300 }}
                  value={formik.values.Bio}
                  onChange={(val) => {
                    if (val.target.value === ' ' || val.target.value === '.') {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.Bio && formik.errors.Bio}
                />
              </div>
            ) : null}

            {/* {type === "dealer" || type === "store" ? (
              <>
                <div className="control_group w_50">
                  <label>Store Opening Time</label>
                  <TextField
                    type="date"
                    variant="outlined"
                    placeholder="Enter here"
                    fullWidth
                    hiddenLabel
                  />
                </div>
                <div className="control_group w_50">
                  <label>Store Closing Time</label>
                  <TextField
                    type="date"
                    variant="outlined"
                    placeholder="Enter here"
                    fullWidth
                    hiddenLabel
                  />
                </div>
                <div className="control_group w_100">
                  <label>Bio</label>
                  <TextField
                    type="date"
                    variant="outlined"
                    placeholder="Enter here"
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={4}
                    hiddenLabel
                  />
                </div>
              </>
            ) : null} */}
            <div className="control_group w_50">
              <label> Address</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                name="address"
                id="address"
                className="text_field"
                inputProps={{ maxLength: 200 }}
                value={formik.values.address}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.address && formik.errors.address}
              />
            </div>
            <div className="control_group w_50">
              <label> City</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                name="city"
                id="city"
                className="text_field"
                inputProps={{ maxLength: 200 }}
                value={formik.values.city}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.city && formik.errors.city}
              />
            </div>
            {/* <div className="control_group w_50">
              <label> Pin Code</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                name="pincode"
                id="pincode"
                className="text_field"
                inputProps={{ maxLength: 10 }}
                value={formik.values.pincode}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else if (isNumber(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.pincode && formik.errors.pincode}
              />
            </div> */}

            <h5 style={{ fontSize: '16px', fontWeight: 600 }}>Bank Details</h5>
            <div className="control_group w_50">
              <label> Bank Name</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                name="bankName"
                className="text_field"
                id="bankName"
                inputProps={{ maxLength: 100 }}
                value={formik.values.bankName}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.bankName && formik.errors.bankName}
              />
            </div>
            <div className="control_group w_50">
              <label> Account Holder's Name</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                className="text_field"
                name="accountHolederName"
                id="accountHolederName"
                inputProps={{ maxLength: 100 }}
                value={formik.values.accountHolederName}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.accountHolederName && formik.errors.accountHolederName}
              />
            </div>
            <div className="control_group w_50">
              <label> Branch Name</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                className="text_field"
                name="branchName"
                id="branchName"
                inputProps={{ maxLength: 100 }}
                value={formik.values.branchName}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.branchName && formik.errors.branchName}
              />
            </div>
            <div className="control_group w_50">
              <label> Bank Code</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                className="text_field"
                name="bankCode"
                id="bankCode"
                inputProps={{ maxLength: 16 }}
                value={formik.values.bankCode}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else if (isNumber(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.bankCode && formik.errors.bankCode}
              />
            </div>
          </div>
          <div className="form_btn end">
            <Button className="btn btn_secondary" type="submit">
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default EditProfile;
